// src/features/integrity.ts
import { emitSafeTamper } from "../core/events";

/** hafif string hash */
function fastHash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return h >>> 0;
}

function fnSrcHash(fn: any): number {
  try {
    const src = Function.prototype.toString.call(fn);
    return fastHash(src);
  } catch {
    return -1;
  }
}

/** ---- Native API Snapshot Guard (hash tabanlı) ---- */
export function startNativeGuard(opts?: { intervalMs?: number; debug?: boolean }) {
  if (typeof window === "undefined") return () => {};
  const intervalMs = opts?.intervalMs ?? 4000;

  // Hem window hem prototype için baseline hash’leri al
  const baseline = {
    winAdd: fnSrcHash(window.addEventListener),
    winRemove: fnSrcHash(window.removeEventListener),
    winDispatch: fnSrcHash(window.dispatchEvent),
    protoAdd: fnSrcHash(EventTarget.prototype.addEventListener),
    protoRemove: fnSrcHash(EventTarget.prototype.removeEventListener),
    protoDispatch: fnSrcHash(EventTarget.prototype.dispatchEvent),
    CustomEvent: fnSrcHash(window.CustomEvent),
    setTimeout: fnSrcHash(window.setTimeout),
    setInterval: fnSrcHash(window.setInterval),
    fetch: typeof window.fetch === "function" ? fnSrcHash(window.fetch) : -1,
  };

  if (opts?.debug) {
    console.log("[JSentinel] NativeGuard started (hashes)", baseline);
    (window as any).__JSENTINEL_NATIVE_SNAPSHOT__ = baseline;
  }

  const check = () => {
    try {
      const now = {
        winAdd: fnSrcHash(window.addEventListener),
        winRemove: fnSrcHash(window.removeEventListener),
        winDispatch: fnSrcHash(window.dispatchEvent),
        protoAdd: fnSrcHash(EventTarget.prototype.addEventListener),
        protoRemove: fnSrcHash(EventTarget.prototype.removeEventListener),
        protoDispatch: fnSrcHash(EventTarget.prototype.dispatchEvent),
        CustomEvent: fnSrcHash(window.CustomEvent),
        setTimeout: fnSrcHash(window.setTimeout),
        setInterval: fnSrcHash(window.setInterval),
        fetch: typeof window.fetch === "function" ? fnSrcHash(window.fetch) : -1,
      };

      const changed =
        now.winAdd !== baseline.winAdd ||
        now.winRemove !== baseline.winRemove ||
        now.winDispatch !== baseline.winDispatch ||
        now.protoAdd !== baseline.protoAdd ||
        now.protoRemove !== baseline.protoRemove ||
        now.protoDispatch !== baseline.protoDispatch ||
        now.CustomEvent !== baseline.CustomEvent ||
        now.setTimeout !== baseline.setTimeout ||
        now.setInterval !== baseline.setInterval ||
        now.fetch !== baseline.fetch;

      if (changed) emitSafeTamper("natives-patched");
    } catch {
      // swallow
    }
  };

  const id = setInterval(check, intervalMs);

  // anında tetiklemek için debug helper
  (window as any).__JSENTINEL_CHECK_NATIVES__ = check;
  return () => clearInterval(id);
}

/** ---- Self-Integrity (kritik fonksiyon toString-hash) ---- */
export function startIntegrityGuard(
  targets: Record<string, Function>,
  opts?: { intervalMs?: number; debug?: boolean },
) {
  if (typeof window === "undefined") return () => {};
  const intervalMs = opts?.intervalMs ?? 5000;

  const baseline: Record<string, number> = {};
  for (const [k, fn] of Object.entries(targets)) {
    baseline[k] = fnSrcHash(fn);
  }

  if (opts?.debug) {
    console.log("[JSentinel] IntegrityGuard started", baseline);
    (window as any).__JSENTINEL_INTEGRITY_BASELINE__ = baseline;
  }

  const id = setInterval(() => {
    try {
      for (const [k, fn] of Object.entries(targets)) {
        const cur = fnSrcHash(fn);
        if (cur !== baseline[k]) {
          emitSafeTamper(`code-mutated:${k}`);
          break;
        }
      }
    } catch {}
  }, intervalMs);

  return () => clearInterval(id);
}
